import React, { Fragment, useState, useEffect } from "react";
import ModalModern from "../../ModalModern";
import ModernPopupHeader from "../../common/ModernPopupHeader";
import ModernPopupBody from "../../common/ModernPopupBody";
import { getInstitutionSyncStates } from "../../../../../api/finances/institutionAPI";
import { fetchCreditRules } from "../../../../../api/finances/guardianAPI";
import LoadingPlaceholder from "../../../loading/LoadingPlaceholder";

function CancelSubscriptionModal({
  isOpen,
  onClose,
  subscription,
  onConfirm,
  isSubmitting = false,
}) {
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(null);

  const [creditRules, setCreditRules] = useState([]);
  const [selectedCreditRuleId, setSelectedCreditRuleId] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);

      // Fetch institutions and credit safeguard rules concurrently
      Promise.all([
        getInstitutionSyncStates().catch((err) => {
          console.error("Error fetching user institutions:", err);
          return null;
        }),
        fetchCreditRules().catch((err) => {
          console.error("Error fetching user credit rules:", err);
          return null;
        }),
      ]).then(([instData, creditData]) => {
        // 1. Process Institutions
        const instList = instData?.institutions || instData || [];
        setInstitutions(instList);

        // 2. Process Credit Rules
        if (creditData && creditData.success) {
          setCreditRules(creditData.data || []);
        } else {
          setCreditRules([]);
        }

        setLoading(false);
      });
    } else {
      setInstitutions([]);
      setSelectedInstitutionId(null);
      setCreditRules([]);
      setSelectedCreditRuleId(null);
    }
  }, [isOpen]);

  if (!isOpen || !subscription) return null;

  const handleToggleInstitution = (uuid) => {
    setSelectedInstitutionId((prevId) => (prevId === uuid ? null : uuid));
  };

  const handleToggleCreditRule = (uuid) => {
    setSelectedCreditRuleId((prevId) => (prevId === uuid ? null : uuid));
  };

  const handleUnsubscribeSubmit = () => {
    const institutionToKeep = institutions.find(
      (ins) => ins.uuid === selectedInstitutionId,
    );

    const creditRuleToKeep = creditRules.find(
      (rule) => rule.uuid === selectedCreditRuleId,
    );

    onConfirm({
      subscriptionId: subscription.id,
      keepInstitution: institutionToKeep || null,
      keepCreditRule: creditRuleToKeep || null,
    });
  };

  return (
    <Fragment>
      <ModalModern onClose={onClose}>
        <ModernPopupHeader onClose={onClose}>
          <div className="header-content-modern-row">
            <h3 className="transaction-popup-title" style={{ margin: 0 }}>
              Cancel Subscription
            </h3>
          </div>
        </ModernPopupHeader>

        <ModernPopupBody>
          <div className="mp-col-container" style={{ gap: "20px" }}>
            <div>
              <p>
                You are about to unsubscribe from the{" "}
                <strong>{subscription.service}</strong> feature.
              </p>
              <span className="light-description">
                Please note that canceling this feature will permanently delete
                or limit its associated data, including your
                <strong style={{ fontWeight: "400" }}>
                  {" "}
                  Budgets, Budget History, and custom safeguard rules
                </strong>
                .
              </span>
            </div>

            {/* Step 1: Keep 1 Institution Selection */}
            <div
              className="audit-protection-content"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div>
                <p style={{ fontWeight: 600, margin: "0 0 4px 0" }}>
                  Keep one institution connected:
                </p>
                <span className="light-description">
                  You can choose only one of your connected institutions to keep
                  active in your account. The remaining institutions will be
                  deleted immediately.
                </span>
              </div>

              {loading ? (
                <LoadingPlaceholder rows={1} type="loading-card" />
              ) : institutions.length === 0 ? (
                <div
                  style={{
                    padding: "8px 0",
                    color: "var(--grey-text)",
                    fontSize: "14px",
                  }}
                >
                  No connected institutions found.
                </div>
              ) : (
                institutions.map((inst) => (
                  <div
                    key={inst.uuid}
                    className="audit-protection-checkbox-container"
                    onClick={() => handleToggleInstitution(inst.uuid)}
                    style={{ cursor: "pointer", marginBottom: 0 }}
                  >
                    <label
                      className="audit-protection-checkbox-label"
                      style={{
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div className="audit-protection-checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={selectedInstitutionId === inst.uuid}
                          readOnly
                          className="audit-protection-checkbox"
                        />
                      </div>
                      <span>{inst.institution_name}</span>
                    </label>
                  </div>
                ))
              )}
            </div>

            {/* Step 2: Keep 1 Credit Safeguard Rule Selection */}
            <div
              className="audit-protection-content"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div>
                <p style={{ fontWeight: 600, margin: "0 0 4px 0" }}>
                  Keep one credit safeguard rule:
                </p>
                <span className="light-description">
                  Your free plan allows 1 credit safeguard rule. Choose which
                  rule to keep active. Any unselected rules will be deleted.
                </span>
              </div>

              {loading ? (
                <LoadingPlaceholder rows={1} type="loading-card" />
              ) : creditRules.length === 0 ? (
                <div
                  style={{
                    padding: "8px 0",
                    color: "var(--grey-text)",
                    fontSize: "14px",
                  }}
                >
                  No active credit safeguard rules found.
                </div>
              ) : (
                creditRules.map((rule) => (
                  <div
                    key={rule.uuid}
                    className="audit-protection-checkbox-container"
                    onClick={() => handleToggleCreditRule(rule.uuid)}
                    style={{ cursor: "pointer", marginBottom: 0 }}
                  >
                    <label
                      className="audit-protection-checkbox-label"
                      style={{
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="audit-protection-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={selectedCreditRuleId === rule.uuid}
                            readOnly
                            className="audit-protection-checkbox"
                          />
                        </div>
                        <span>{rule.name}</span>
                      </div>
                      <span style={{ fontWeight: 600, fontSize: "13px" }}>
                        $
                        {rule.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </label>
                  </div>
                ))
              )}
            </div>

            {/* Warnings block when selections are empty */}
            {!loading && (!selectedInstitutionId || !selectedCreditRuleId) && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  backgroundColor: "#FFFBEB",
                  border: "1px solid #FCD34D",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginTop: "4px",
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/warning_amber.svg`}
                  alt="Warning"
                  style={{ width: "20px", height: "20px", marginTop: "2px" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400E",
                    }}
                  >
                    Attention Needed
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#B45309",
                      lineHeight: "1.4",
                    }}
                  >
                    {!selectedInstitutionId && !selectedCreditRuleId
                      ? "No institution or credit rule selected. All connected institutions and credit safeguard rules will be removed."
                      : !selectedInstitutionId
                      ? "No institution selected. All connected institutions will be removed."
                      : "No credit safeguard rule selected. All credit safeguard rules will be removed."}
                  </span>
                </div>
              </div>
            )}

            {/* Actions Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                borderTop: "1px solid #E5E7EB",
                paddingTop: "20px",
              }}
            >
              <button
                type="button"
                onClick={onClose}
                className="cta-button secondary"
                disabled={loading || isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUnsubscribeSubmit}
                className="cta-button primary"
                disabled={loading || isSubmitting}
                style={{ backgroundColor: "#FF6D4B" }}
              >
                {isSubmitting ? "Processing..." : "Unsubscribe"}
              </button>
            </div>
          </div>
        </ModernPopupBody>
      </ModalModern>
    </Fragment>
  );
}

export default CancelSubscriptionModal;
